// Custom hook for enhanced Solana wallet logging with server integration
import { useCallback, useRef } from 'react';
import { getSolanaLoggingService } from '../services/solana-logging.service';
import { useAccount } from 'wagmi';
import { useWallet } from '@solana/wallet-adapter-react';
import { useAuth } from './useAuth';

interface SolanaLoggerHook {
  logConnectionAttempt: (data?: Record<string, any>) => Promise<void>;
  logConnectionSuccess: (connectionTime?: number) => Promise<void>;
  logConnectionError: (error: Error, context?: Record<string, any>) => Promise<void>;
  logAssociationAttempt: (isAutoAssociation?: boolean) => Promise<void>;
  logAssociationSuccess: (associationId?: string) => Promise<void>;
  logAssociationError: (error: Error, phase: string, context?: Record<string, any>) => Promise<void>;
  logEVMPurchaseWithSolanaContext: (
    action: 'attempt' | 'success' | 'error',
    idoId: string,
    amount: string,
    currency: string,
    options?: {
      txHash?: string;
      error?: Error;
      isAssociated?: boolean;
      requiresSolana?: boolean;
    }
  ) => Promise<void>;
  flushLogs: () => Promise<void>;
  getConnectionStats: () => Promise<any>;
}

export const useSolanaLogger = (): SolanaLoggerHook => {
  const { address: evmAddress } = useAccount();
  const { publicKey, wallet } = useWallet();
  const isAuth = useAuth();
  const solanaLogger = getSolanaLoggingService();
  const connectionStartTime = useRef<number>();

  const logConnectionAttempt = useCallback(async (data?: Record<string, any>) => {
    connectionStartTime.current = Date.now();
    
    await solanaLogger.logSolanaConnectionAttempt({
      evmAddress,
      isAuth,
      availableWallets: 0, // Will be updated by caller
      walletNames: [],
      userAgent: navigator.userAgent,
      ...data
    });
  }, [evmAddress, isAuth, solanaLogger]);

  const logConnectionSuccess = useCallback(async (connectionTime?: number) => {
    const actualConnectionTime = connectionTime || 
      (connectionStartTime.current ? Date.now() - connectionStartTime.current : undefined);

    await solanaLogger.logSolanaConnectionSuccess({
      success: true,
      walletAddress: publicKey?.toString(),
      connectionTime: actualConnectionTime,
      walletName: wallet?.adapter.name
    });
  }, [publicKey, wallet, solanaLogger]);

  const logConnectionError = useCallback(async (error: Error, context?: Record<string, any>) => {
    await solanaLogger.logSolanaConnectionError(error, {
      evmAddress,
      availableWallets: 0,
      walletName: wallet?.adapter.name,
      ...context
    });
  }, [evmAddress, wallet, solanaLogger]);

  const logAssociationAttempt = useCallback(async (isAutoAssociation?: boolean) => {
    if (!evmAddress || !publicKey) return;

    await solanaLogger.logSolanaAssociationAttempt({
      evmAddress,
      solanaAddress: publicKey.toString(),
      isAutoAssociation
    });
  }, [evmAddress, publicKey, solanaLogger]);

  const logAssociationSuccess = useCallback(async (associationId?: string) => {
    if (!evmAddress || !publicKey) return;

    await solanaLogger.logSolanaAssociationSuccess({
      success: true,
      evmAddress,
      solanaAddress: publicKey.toString(),
      associationId,
      createdAt: new Date().toISOString()
    });
  }, [evmAddress, publicKey, solanaLogger]);

  const logAssociationError = useCallback(async (
    error: Error, 
    phase: string, 
    context?: Record<string, any>
  ) => {
    await solanaLogger.logSolanaAssociationError(error, {
      evmAddress,
      solanaAddress: publicKey?.toString(),
      phase,
      ...context
    });
  }, [evmAddress, publicKey, solanaLogger]);

  const logEVMPurchaseWithSolanaContext = useCallback(async (
    action: 'attempt' | 'success' | 'error',
    idoId: string,
    amount: string,
    currency: string,
    options: {
      txHash?: string;
      error?: Error;
      isAssociated?: boolean;
      requiresSolana?: boolean;
    } = {}
  ) => {
    // Only log if this IDO requires Solana association
    if (!options.requiresSolana) return;
    
    await solanaLogger.logSolanaWalletPurchase(`evm_purchase_with_solana_context_${action}`, {
      idoId,
      amount,
      currency,
      evmAddress,
      solanaAddress: publicKey?.toString(),
      isAssociated: options.isAssociated ?? false,
      txHash: options.txHash,
      error: options.error,
      purchaseMethod: 'EVM', // Clarify that purchase is via EVM, not Solana
      solanaRequirement: options.requiresSolana
    });
  }, [evmAddress, publicKey, solanaLogger]);

  const flushLogs = useCallback(async () => {
    await solanaLogger.flushLogs();
  }, [solanaLogger]);

  const getConnectionStats = useCallback(async () => {
    return await solanaLogger.getConnectionStatistics();
  }, [solanaLogger]);

  return {
    logConnectionAttempt,
    logConnectionSuccess,
    logConnectionError,
    logAssociationAttempt,
    logAssociationSuccess,
    logAssociationError,
    logEVMPurchaseWithSolanaContext,
    flushLogs,
    getConnectionStats
  };
};